import * as React from 'react';
import { translate } from 'react-i18next';
import * as i18n from 'i18next';
import * as c from 'models/constants';
import { ICreateShopObject } from 'models/shop/createshop';
import { IStore } from 'redux/IStore';
import { Field, FieldArray, reduxForm, getFormSyncErrors } from 'redux-form';
import { BottomButton, Button, ErrorSummary, GeoMap } from 'components';
import { PortalPageSection } from 'components/PortalStructure';
import {
	PortalDatePicker,
	PortalInputField,
	PortalLocationAutocomplete,
	PortalSelectMenu,
	PortalCheckboxField,
} from 'components/FormControls';
import { createDomainValueOptions, fullPhoneNumber, lowerFirstObjectPropertiesNames } from 'helpers/common';
import { withRouter, WithRouterProps } from 'react-router';
import {
	createEndTimeValidationFuntion,
	email,
	phoneNumber,
	required,
	scrollErrorIntoView,
} from 'helpers/validationattributes';
import { ReactDatePickerProps } from 'react-datepicker';
import { FieldArrayFieldsProps } from 'models/common/redux-form';
import { ICreateShopProfile, IShopProfile } from 'models/shop/shopprofile';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { IGeoLocation } from 'models/map';
import { getCountryCallingCodeDomainValues, getCountryDomainValues } from 'redux/modules/domainvalues';
import { IManageShop } from 'models/shop/manageshop';
import { DayOfWeek } from 'models/shop/office';
import { clearFormData } from 'redux/modules/shop/manageshop';
import { renderOpeningTimeReadOnly } from '../../Helpers';
import { Dispatch } from 'redux';
import { preventRouterLeave } from 'helpers/hooks';
import { actions as notifActions } from 'redux-notifications';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
const { connect } = require('react-redux');

type DataType =
	| ICreateShopObject<ICreateShopProfile>
	| IManageShop<IShopProfile & { tmpTimesLocation?: string; mainLocation?: string; fromCountryKey?: number }>;

interface IOwnProps extends WithRouterProps<{}, {}> {
	readOnly?: boolean;
	toggleReadOnly?: () => void;
	redux: 'createShop' | 'manageShop';
	previousPage?: () => void;
	onSubmit?: ((form: ICreateShopObject) => void) | ((form: ICreateShopObject<IShopProfile>) => void);
}

interface IProps
	extends IOwnProps,
		InjectedFormProps<DataType>,
		ITranslateProps,
		ReturnType<typeof stateToProps>,
		ReturnType<typeof dispatchToProps> {}

interface IState {
	locationIndex: number;
	timezone: string;
}

const weekMS = [1, 2, 3, 4, 5, 6, 0];

const timePickerProps = (): Partial<ReactDatePickerProps> => ({
	showTimeSelect: true,
	showTimeSelectOnly: true,
	timeIntervals: c.MIN_TIME_INTERVAL_MINUTES,
	dateFormat: 'HH:mm',
	timeFormat: 'HH:mm',
	shouldCloseOnSelect: true,
});

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopLocationsForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);

		this.setDefaultLocation = this.setDefaultLocation.bind(this);
		this.addLocation = this.addLocation.bind(this);

		this.renderOffices = this.renderOffices.bind(this);
		this.renderLocation = this.renderLocation.bind(this);
		this.validateArray = this.validateArray.bind(this);
		this.validateLocationsHaveRequiredFields = this.validateLocationsHaveRequiredFields.bind(this);

		this.state = {
			locationIndex: 0,
			timezone: null,
		};
	}

	private validateArray(value: any[]) {
		return value && Array.isArray(value) && value.length ? undefined : this.props.t('Validation_Client_Field_Required');
	}

	private validateLocationsHaveRequiredFields() {
		let returnValue = undefined;
		const { currentFormValues, t } = this.props;

		currentFormValues.shop.offices.forEach((office) => {
			if (!(office?.email && office.email != '') && !(office?.phoneNumber && office.phoneNumber != '')) {
				returnValue = t('Validation_Field_EmailAndNumber');
			}
		});

		return returnValue;
	}

	public componentDidMount() {
		this.props.getCountryCallingCodeDomainValues();
		this.props.getCountryDomainValues();
		this.setOpeningTimesLocation();
	}

	public componentWillUnmount(): void {
		this.props.clearFormData();
	}

	private get offices() {
		return this.props.currentFormValues.shop?.offices || [];
	}

	public componentDidUpdate(): void {
		this.setDefaultLocation();
		this.setOpeningTimesLocation();
	}

	public onSubmit(form: ICreateShopObject) {
		this.props.onSubmit(JSON.parse(JSON.stringify(form)));
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'officeLocation':
			case 'location':
				return t('Shop_ManageShop_General_Label_Location');
			case 'offices':
			case 'shop.offices':
				return t('Shop_ManageShop_Location_Label_AddLocation');
			// return t('Shop_ManageShop_Location_Error_Locations');
			case 'phoneNumber':
				return t('Shop_ManageShop_Offices_ManageOffice_Label_PhoneNumber');
			case 'email':
				return t('Shop_ManageShop_Offices_ManageOffice_Label_Email');
			default:
				return key;
		}
	}

	/**
	 * Change only if we are not in readonly
	 */
	private change(key: string, value: any) {
		if (this.props.readOnly) {
			return;
		}
		this.props.change(key, value);
	}

	private setDefaultLocation(index?: number | undefined) {
		requestAnimationFrame(() => {
			const { change, currentFormValues } = this.props;
			if (!this.offices.length || (index !== undefined && index < 0)) {
				change('shop.mainLocation', null);
				return this.setOpeningTimesLocation();
			}
			const currentIndexes: number[] = [];
			for (let i = 0; i < this.offices.length; i++) {
				if (this.offices[i].isDefault) {
					currentIndexes.push(i);
				}
			}
			const currentMain = (currentFormValues.shop as ICreateShopProfile)?.mainLocation;
			const currentIndex = currentIndexes.length ? currentIndexes[0] : -1;
			if (index === undefined && currentIndex >= 0) {
				index = currentIndex;
			}
			const newIndex = Math.min(index || 0, this.offices.length - 1);
			if (currentMain !== String(newIndex)) {
				change('shop.mainLocation', String(newIndex));
			}
			if (currentIndex === newIndex && currentIndexes.length === 1) {
				return this.setOpeningTimesLocation();
			}

			this.offices.forEach((o, i) => {
				const isDefault = i === newIndex;
				if (Boolean(o.isDefault) !== isDefault) {
					this.change('shop.offices[' + i + '].isDefault', isDefault);
				}
			});

			change('shop.mainLocation', String(newIndex));

			return this.setOpeningTimesLocation();
		});
	}

	private addLocation(_: React.ChangeEvent<any>, location: IGeoLocation) {
		if (!location || this.props.readOnly || typeof location !== 'object') {
			return;
		}
		const { change, currentFormValues } = this.props;
		const offices = [...(currentFormValues.shop?.offices || [])];

		const copy = offices.find((l) => l.location === location.fullLocation);
		if (copy) {
			return;
		}

		// https://developers.google.com/maps/documentation/javascript/geocoding#GeocodingAddressTypes
		const typesMaps = {
			name: [
				'point_of_interest',
				'park',
				'airport',
				'natural_feature',
				'premise',
				'subpremise',
				'intersection',
				'point_of_interest',
				'landmark',
				'establishment',
			],
			address: ['street_address', 'route'],
			streetNumber: ['street_number'],
			city: ['locality', 'administrative_area_level_3', 'sublocality', 'postal_town'],
			postalCode: ['postal_code', 'post_box'],
			country: ['country'],
			province: [
				'administrative_area_level_5',
				'administrative_area_level_4',
				'administrative_area_level_2',
				'administrative_area_level_1',
			],
		};

		const getField = (
			place: google.maps.GeocoderResult,
			includeTypes: string[],
			returnOnlyOne = false,
			returnShortName = false,
		): string => {
			const arr: string[] = [];
			if (typeof place === 'object') {
				for (const item of place.address_components) {
					for (const type of item.types) {
						if (includeTypes.includes(type)) {
							if (returnOnlyOne) {
								return item.long_name;
							}
							if (returnShortName) {
								return item.short_name;
							}
							arr.push(item.long_name);
							break;
						}
					}
				}
			}
			return arr.join(', ');
		};
		const joinOmmit = (arr, delimiter = ' ') => arr.filter((item) => item?.length).join(delimiter);
		const city = getField(location, typesMaps.city, true);
		const streetNumber = getField(location, typesMaps.streetNumber); // currently unused
		const name = getField(location, typesMaps.name) || location.fullLocation;
		const address = getField(location, typesMaps.address);
		const postalCode = getField(location, typesMaps.postalCode);
		const province = getField(location, typesMaps.province, true);
		const country = getField(location, typesMaps.country);
		const locationArr = city ? [joinOmmit([address, streetNumber]), city] : [city, province];
		const area = getField(location, typesMaps.province.slice(-1), true); // Administrative area 1, example: Nürnberg returns Bavaria
		const shortName = getField(location, typesMaps.country, false, true); // Alpha 2 code

		// Find the selected country in domain values for office country key
		const countryObject = this.props?.countryDomainValues.find(({ alpha2Code }) => alpha2Code === shortName);

		const shippingCountryKey =
			this.props?.currentFormValues?.shop?.shippingFromCountryKey ||
			this.props?.currentFormValues?.shop?.fromCountryKey;

		// Safety ifs that prevent user from adding new offices (should not happen as long as alpha2code exists)
		if (!countryObject) {
			console.error('No matching country code found!');
			return;
		}
		if (shippingCountryKey !== countryObject.key) {
			console.error('Location that you wanted to add is not in the same country as the shop!');
			return;
		}
		if (!city) {
			this.props.showNotification(this.props.t('ShopBusinessRules_ShippingCityRequired'), 'warning');
			requestAnimationFrame(() => {
				change('shop.officeLocation', '');
			});
			return;
		}

		offices.push({
			shopId: '',
			location: joinOmmit(locationArr, ', '),
			name,
			address,
			city,
			postalCode,
			country,
			countryKey: countryObject.key,
			area,
			province: province,
			geoLocationLatitude: location.geometry.location.lat(),
			geoLocationLongitude: location.geometry.location.lng(),
			officeTimes: [],
			staff: [],
		});
		change('shop.offices', offices);
		requestAnimationFrame(() => {
			change('shop.officeLocation', '');
			change('shop.tmpTimesLocation', String(offices.length - 1));
		});
		this.setDefaultLocation();
	}

	private setOpeningTimesLocation() {
		const { change, currentFormValues } = this.props;
		const max = (currentFormValues.shop?.offices?.length || 0) - 1;
		const currentOpening = Number(currentFormValues.shop.tmpTimesLocation) || undefined;
		if (max === -1 && currentOpening >= 0) {
			change('shop.tmpTimesLocation', undefined);
		}
		if (max >= 0) {
			const nextOpening = Math.min(max, currentOpening || 0);
			if (nextOpening !== currentOpening) {
				change('shop.tmpTimesLocation', String(nextOpening));
			}
		}
	}

	private addRemoveOpeningTime(locationIndex: string, dayMS: DayOfWeek, add: boolean) {
		const { currentFormValues, change } = this.props;
		const key = 'shop.offices[' + locationIndex + '].officeTimes';
		const index = Number(locationIndex);
		if (isNaN(index) || index < 0) {
			return;
		}
		const current = [...(currentFormValues.shop.offices[index]?.officeTimes || [])].map(
			lowerFirstObjectPropertiesNames,
		);
		const valueIndex = current.findIndex((ot) => ot.day === dayMS);
		if (valueIndex < 0 && add) {
			current.push({
				day: dayMS,
				openTime: '',
				closeTime: '',
			});
		} else if (valueIndex >= 0 && !add) {
			current.splice(valueIndex, 1);
		}
		change(key, current);
	}

	private renderOpeningTime(officeIndex: string, dayMS: DayOfWeek) {
		const { t } = this.props;
		const index = Number(officeIndex);
		if (isNaN(index) || index < 0) {
			return null;
		}
		const valueIndex = (this.offices[index]?.officeTimes || [])
			.map(lowerFirstObjectPropertiesNames)
			.findIndex((ot) => ot.day === dayMS);
		const value = valueIndex < 0 ? undefined : this.offices[index]?.officeTimes[valueIndex];
		const validateClosingTime = [required];
		let times = (
			<div className="col-md-6 col-xs-12 text-right note">
				{t('Shop_ManageShop_Location_Label_OpeningTimes_Closed')}
			</div>
		);
		if (value) {
			if (value.openTime) {
				const min = value.openTime;
				const minDate = momentWithTimezone(this.state.timezone, min);
				if (minDate) {
					validateClosingTime.push(createEndTimeValidationFuntion(min, t, this.state.timezone));
				}
			}
			times = (
				<>
					<div className="col-md-3 col-xs-6">
						<Field
							name={'shop.offices[' + officeIndex + '].officeTimes[' + valueIndex + '].openTime'}
							type="text"
							component={PortalDatePicker}
							label={t('Shop_ManageShop_Location_Label_OpeningTimes_From')}
							validate={[required]}
							{...timePickerProps()}
							timezone={this.state.timezone}
						/>
					</div>
					<div className="col-md-3 col-xs-6">
						<Field
							name={'shop.offices[' + officeIndex + '].officeTimes[' + valueIndex + '].closeTime'}
							type="text"
							component={PortalDatePicker}
							label={t('Shop_ManageShop_Location_Label_OpeningTimes_To')}
							validate={validateClosingTime}
							{...timePickerProps()}
							timezone={this.state.timezone}
						/>
					</div>
				</>
			);
		}
		const inputProp = {
			checked: Boolean(value),
			onChange: () => this.addRemoveOpeningTime(officeIndex, dayMS, !value),
		};
		return (
			<React.Fragment key={dayMS}>
				<div className="row align-items-center">
					<div className="col-md-6 col-xs-12">
						<PortalCheckboxField input={inputProp} label={t('Day_' + dayMS)} type="checkbox" />
						{/* <Field name={'shop.offices[' + locationIndex + '].officeTimes[' + day + '].open'} label={t('Day_' + day)} component={PortalCheckboxField} type="checkbox" /> */}
					</div>
					{times}
				</div>
				<hr />
			</React.Fragment>
		);
	}

	private renderLocation(
		_name: string,
		i: number,
		all: FieldArrayFieldsProps<ICreateShopProfile['offices'][number]>,
	): JSX.Element {
		const { readOnly } = this.props;
		return (
			<React.Fragment key={i}>
				<dt>{i + 1}</dt>
				<dd>
					<span>{all.get(i)?.location}</span>
					{readOnly || all.get(i)?.isDefault ? null : (
						<button type="button" className="btnIcon" onClick={() => all.remove(i)}>
							<img src={require('containers/Html/icons/delete_2.svg')} />
						</button>
					)}
				</dd>
			</React.Fragment>
		);
	}

	private renderOffices({
		fields,
	}: {
		fields: FieldArrayFieldsProps<ICreateShopProfile['offices'][number]>;
	}): JSX.Element {
		if (fields.length) {
			return <dl className="list">{fields.map(this.renderLocation)}</dl>;
		}
		return null;
	}

	public render() {
		const {
			handleSubmit,
			t,
			formErrors,
			submitFailed,
			location,
			userPreferences,
			currentFormValues,
			countryCallingCodes,
			readOnly,
			shopPicture,
		} = this.props;

		// shippingFormCountryKey is for adding offices, fromCountryKey is for adding salons
		const countryObject = this.props?.countryDomainValues?.find(
			({ key }) =>
				key === currentFormValues?.shop?.shippingFromCountryKey || key === currentFormValues?.shop?.fromCountryKey,
		);

		const mainLocation = this.offices.find((o) => o.isDefault);
		const officesOptions = this.offices.map((l, index) => ({
			key: String(index),
			value: l.location,
		}));

		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', countryCallingCodes);

		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.createShop ? this.props.createShop.error : false,
			message: this.props.createShop ? this.props.createShop.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		const currentOfficeIdx = currentFormValues.shop.tmpTimesLocation || '0';
		const currentOffice = this.offices[Number(currentOfficeIdx)];
		const mainLocationName = mainLocation ? mainLocation.name || mainLocation.location : '';
		const timezone = currentOffice?.zoneInfo;
		if (this.state.timezone !== timezone) {
			this.setState({ timezone });
		}

		const getFullPhone = (office: typeof currentOffice): string => {
			if (office.fullPhone) {
				return office.fullPhone;
			}
			let phone = callingCodes.find((cc) => cc.key === currentOffice.callingCodeKey)?.value || '';
			phone += ' ' + office.phoneNumber;
			return phone.trim();
		};

		return (
			<form onSubmit={handleSubmit(this.onSubmit)}>
				<h1 className="text-center pageTitle">{t('Shop_ManageShop_Location_Title')}</h1>
				<div className="profileError">
					<ErrorSummary {...errorSummaryProps} />
				</div>
				<PortalPageSection
					title={t('Shop_ManageShop_Location_Label_Locations')}
					readOnly={this.props.readOnly}
					toggleEdit={this.props.toggleReadOnly}
				>
					{readOnly ? null : (
						<div className="row align-items-center">
							<p className="col-md-3 col-xs-12">{t('Shop_ManageShop_Location_Label_AddLocation')}</p>
							<div className="col-md-9 col-xs-12">
								<Field
									className="rounded"
									name="shop.officeLocation"
									onChange={this.addLocation}
									component={PortalLocationAutocomplete}
									limitCountryCodes={countryObject?.alpha2Code} // Limits the shop's google maps search to the selected shipping country
									includeDetails={true}
									type="text"
									label={t('Shop_ManageShop_Location_Label_SearchLocation')}
								/>
							</div>
						</div>
					)}
					<GeoMap
						className="small inset"
						items={
							currentFormValues.shop?.offices?.map((e) =>
								Object.assign(
									{ shop: { picture: currentFormValues.shop?.imageUrl || currentFormValues?.picture || shopPicture } },
									e,
								),
							) || []
						}
						mapSettings={{
							streetViewControl: false,
						}}
						userPicture={userPreferences.profilePicture}
					/>
					<FieldArray name="shop.offices" component={this.renderOffices} validate={this.validateArray} />
					{this.offices.length > 1 && (
						<>
							{readOnly ? null : <hr />}
							<h5>{t('Shop_ManageShop_Location_Label_MainLocation')}</h5>
							{readOnly ? (
								<dl className="list">
									<dt>{t('Shop_ManageShop_Location_Note_MainLocation')}</dt>
									<dd>{mainLocationName}</dd>
								</dl>
							) : (
								<>
									<p className="note">{t('Shop_ManageShop_Location_Note_MainLocation')}</p>
									<Field
										name="shop.mainLocation"
										onChange={(_e: React.ChangeEvent, index) => this.setDefaultLocation(index)}
										component={PortalSelectMenu}
										type="text"
										items={officesOptions}
										multi={false}
										isRequired={true} // To remove the blank option (multi has to be false aswell)
										label={t('Shop_ManageShop_Location_Label_MainLocation')}
										searchable={false}
									/>
								</>
							)}
						</>
					)}
					{readOnly ? null : (
						<Button className="formButton" type="submit">
							{t('BSPortal_Common_Save')}
						</Button>
					)}
				</PortalPageSection>
				{this.offices.length > 0 && (
					<>
						<PortalPageSection
							title={t('Shop_ManageShop_Location_Title_OpeningTimesContact')}
							readOnly={this.props.readOnly}
							toggleEdit={this.props.toggleReadOnly}
						>
							{/*THIS*/}

							{this.offices.length > 1 && (
								<Field
									name="shop.tmpTimesLocation"
									component={PortalSelectMenu}
									type="text"
									items={officesOptions}
									multi={false}
									isRequired={true} // To remove the blank option (multi has to be false aswell)
									label={t('Shop_ManageShop_Location_Label_SalonLocation')}
									validate={this.validateLocationsHaveRequiredFields}
									searchable={false}
								/>
							)}
							<h5>{t('Shop_ManageShop_Location_Title_OpeningTimes')}</h5>
							{readOnly ? (
								<dl className="list">
									{weekMS.map((day) =>
										renderOpeningTimeReadOnly(currentOfficeIdx, day, t, currentFormValues.shop.offices),
									)}
								</dl>
							) : (
								<>
									<hr />
									{weekMS.map((day) => this.renderOpeningTime(currentOfficeIdx, day))}
								</>
							)}
							<h5>{t('Shop_ManageShop_Location_Title_Contact')}</h5>
							{readOnly ? (
								<dl className="list">
									<dt>{t('Shop_ManageShop_Offices_ManageOffice_Label_PhoneNumber')}</dt>
									<dd>{getFullPhone(currentOffice)}</dd>
									<dt>{t('Shop_ManageShop_Offices_ManageOffice_Label_Email')}</dt>
									<dd>{currentOffice?.email}</dd>
								</dl>
							) : (
								<>
									<Field
										name={'shop.offices[' + currentOfficeIdx + '].email'}
										component={PortalInputField}
										isDisabled={readOnly}
										type="text"
										validate={[required, email]}
										label={t('Shop_ManageShop_Offices_ManageOffice_Label_Email')}
										onBlurAction={() => {
											// Trim before and after spaces
											if (currentFormValues?.shop?.offices?.[currentOfficeIdx]?.email?.length) {
												this.props.change(
													`shop.offices[${currentOfficeIdx}].email`,
													(currentFormValues.shop.offices[currentOfficeIdx].email as string).trim(),
												);
											}
										}}
									/>
									<div className="row">
										<div className="col-md-4 col-xs-12">
											<Field
												name={'shop.offices[' + currentOfficeIdx + '].callingCodeKey'}
												component={PortalSelectMenu}
												type="text"
												items={callingCodes}
												withImages={true}
												multi={false}
												isDisabled={readOnly}
												isRequired={true}
												validate={[required]}
												label={t('BSPortal_Common_Phone_Prefix')}
											/>
										</div>
										<div className="col-md-8 col-xs-12">
											<Field
												name={'shop.offices[' + currentOfficeIdx + '].phoneNumber'}
												component={PortalInputField}
												isDisabled={readOnly}
												type="text"
												validate={[required, phoneNumber]}
												label={t('Shop_ManageShop_Offices_ManageOffice_Label_PhoneNumber')}
											/>
										</div>
									</div>
									<Button className="formButton" type="submit">
										{t('BSPortal_Common_Save')}
									</Button>
								</>
							)}
						</PortalPageSection>
					</>
				)}

				<BottomButton type="submit">{t('BSPortal_Common_Next')}</BottomButton>
			</form>
		);
	}
}

const cachedComponents: { [key: string]: ReturnType<typeof createConnected> } = {};

type FormName = typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP;

const stateToProps = (formName: FormName, state: IStore, ownProps: IOwnProps) => {
	let currentFormValues: DataType = null;

	const shopObj: DataType =
		ownProps.redux === 'createShop' ? state[ownProps.redux].createShopObj : state[ownProps.redux];

	if (formName === 'manageshop' || shopObj?.shop?.offices?.length) {
		const defaultOfficeIndex = shopObj?.shop?.offices?.findIndex((off) => off.isDefault) || 0;
		shopObj.shop.tmpTimesLocation = defaultOfficeIndex.toString();
	}

	if (state.form[formName] && state.form[formName].values) {
		currentFormValues = state.form[formName].values;
	} else {
		currentFormValues = shopObj;
	}

	const offices = (shopObj.shop?.offices || []).map((o) => ({
		...o,
		fullPhone: fullPhoneNumber(i18n.t, o.callingCodeKey, o.phoneNumber),
	}));
	const mainLocationIndex = offices.findIndex((o) => o.isDefault);

	return {
		createShop: state[ownProps.redux],
		initialValues: {
			...shopObj,
			shop: {
				...(shopObj.shop || {}),
				mainLocation: offices.length ? (mainLocationIndex >= 0 ? String(mainLocationIndex) : '0') : undefined,
				tmpTimesLocation: offices.length
					? shopObj?.shop?.tmpTimesLocation
						? shopObj.shop.tmpTimesLocation
						: '0'
					: undefined,
				offices: shopObj.shop?.offices ? offices : shopObj.shop?.offices,
			},
		} as DataType,
		currentFormValues,
		userPreferences: state.userPreferences,
		formErrors: getFormSyncErrors(formName)(state),
		userEmail: state.userPreferences ? state.userPreferences.email : '',
		countryCallingCodes: state.domainValues.countryCallingCodes,
		countryDomainValues: state.domainValues.countries,
		shopPicture: state.shopcard.shopcard?.picture,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getCountryCallingCodeDomainValues: () => dispatch(getCountryCallingCodeDomainValues()),
	getCountryDomainValues: () => dispatch(getCountryDomainValues()),
	clearFormData: () => dispatch(clearFormData()),
	showNotification: (message: string, kind: string, dismissAfter = 3000) =>
		dispatch(notifActions.notifSend({ message, kind, dismissAfter })),
});

function createConnected(formName: FormName) {
	let ShopLocationsConnected = reduxForm({
		form: formName,
		destroyOnUnmount: formName === c.FORM_NAME_MANAGE_SHOP,
		forceUnregisterOnUnmount: false,
		enableReinitialize: formName === c.FORM_NAME_MANAGE_SHOP,
		onSubmitFail: () => scrollErrorIntoView(),
	})(ShopLocationsForm) as React.FC<IOwnProps>;

	ShopLocationsConnected = connect(
		(state: IStore, ownProps: IOwnProps) => stateToProps(formName, state, ownProps),
		dispatchToProps,
	)(ShopLocationsConnected);

	return withRouter(ShopLocationsConnected);
}

export function getConnectedShopLocations(formName: typeof c.FORM_NAME_CREATE_SHOP | typeof c.FORM_NAME_MANAGE_SHOP) {
	if (!(formName in cachedComponents)) {
		cachedComponents[formName] = createConnected(formName);
	}
	return cachedComponents[formName];
}

export const ShopLocations = getConnectedShopLocations(c.FORM_NAME_CREATE_SHOP);
